import {
  type AllowedCustomFieldType,
  allowedHeaderCustomFieldTypes,
  type HeaderType,
  headerLabelsType,
  headerTypes,
} from '@tempo-io/roadmap-dto';
import type { TFunction } from 'i18next';
import type { CustomField } from '@/common/api/types/customField';
import type {
  ICustomFieldHeader,
  IDateHeaderSelector,
  IEstimationHeader,
  IItemStatusHeader,
  IPlanHeader,
  IPropertySelectorByID,
  ITeamHeader,
  IVisualizationHeader,
  RMFiscalPeriodType as PeriodType,
} from '@/common/api/types/loopVisualization';

const PERIOD_TYPES: PeriodType[] = ['year', 'half', 'quarter', 'month', 'week'];

export const shouldDisableParentItemSelection = (otherHeaders?: IPropertySelectorByID[]) =>
  otherHeaders?.some(
    (header) =>
      header.type === 'CustomPropertySelectorByID' && header.customPropertyID === 'parentItem',
  );

export const getDisabledFields = (
  selectedHeaders: IPropertySelectorByID[] = [],
  t: TFunction,
  itemLevel?: number,
): Array<DisabledField> => [
  ...getDisabledCreatedByField(selectedHeaders, t),
  ...getDisabledCustomFields(selectedHeaders, t),
  ...getDisabledParentField(selectedHeaders, t, itemLevel),
  ...getDisabledDateHeaderField(selectedHeaders, t),
];

const getDisabledCustomFields = (headers: IPropertySelectorByID[], t: TFunction): DisabledField[] =>
  headers
    .filter((header) => header.type === 'CustomPropertySelectorByID')
    .map((header) => ({
      field: header.customPropertyID,
      reason: t(($) => $.loopVisualization.layout.headers.warnings.fieldAlreadySelected),
    }));

const getHeaderWarning = (key: 'parentItemLevel' | 'fieldAlreadySelected', t: TFunction) =>
  key === 'parentItemLevel'
    ? t(($) => $.loopVisualization.layout.headers.warnings.parentItemLevel)
    : t(($) => $.loopVisualization.layout.headers.warnings.fieldAlreadySelected);

const getDisabledParentField = (
  headers: IPropertySelectorByID[],
  t: TFunction,
  itemLevel?: number,
): DisabledField[] => {
  if (!shouldDisableParentHeader(headers, itemLevel)) return [];

  const warningKey = itemLevel === 1 ? 'parentItemLevel' : 'fieldAlreadySelected';
  return [
    {
      field: 'parentItem',
      reason: getHeaderWarning(warningKey, t),
    },
  ];
};

const getDisabledDateHeaderField = (
  headers: IPropertySelectorByID[],
  t: TFunction,
): DisabledField[] => {
  const otherHeadersAreDate = headers.some((header) => header.type === 'DateSelector');
  if (!otherHeadersAreDate) return [];
  return [
    {
      field: 'itemDate',
      reason: t(($) => $.loopVisualization.layout.headers.warnings.fieldAlreadySelected),
    },
  ];
};

const getDisabledCreatedByField = (
  headers: IPropertySelectorByID[],
  t: TFunction,
): DisabledField[] => {
  const otherHeadersAreDate = headers.some((header) => header.type === 'createdBy');
  if (!otherHeadersAreDate) return [];
  return [
    {
      field: 'createdBy',
      reason: t(($) => $.loopVisualization.layout.headers.warnings.fieldAlreadySelected),
    },
  ];
};

export const shouldDisableParentHeader = (
  headerSelectors: IPropertySelectorByID[],
  itemLevel: number | undefined,
): boolean =>
  itemLevel === 1 || headerSelectors.some((header) => header.type === 'ParentItemSelector');

export type ISelectOption = { label: string; value: string };
export type DisabledField = { field: string; reason: string };

export const MULTI_VALUE_CUSTOM_FIELD_TYPES = [
  'MULTI_PICKLIST',
  'MULTI_CUSTOMER',
  'MULTI_USER',
] as const;

export const getSelectableFields = ({
  fields,
  t,
  includeItemDate,
  allowCustomFields,
  excludeMultiValueFields = false,
  includeMultiCustomerFields = true,
}: {
  fields: CustomField[];
  t: TFunction;
  includeItemDate: boolean;
  allowCustomFields: boolean;
  excludeMultiValueFields?: boolean;
  includeMultiCustomerFields?: boolean;
}): ISelectOption[] => {
  const excludeSet = excludeMultiValueFields
    ? new Set<string>(MULTI_VALUE_CUSTOM_FIELD_TYPES)
    : null;
  const customOptions = allowCustomFields
    ? fields
        .filter(
          (f) =>
            allowedHeaderCustomFieldTypes.includes(f.type as AllowedCustomFieldType) &&
            !excludeSet?.has(f.type) &&
            (includeMultiCustomerFields || f.type !== 'MULTI_CUSTOMER'),
        )
        .map((f) => ({ value: f.id.toString(), label: f.name }))
    : [];

  const headerFieldLabels: Record<HeaderType, string> = {
    status: t(($) => $.loopVisualization.layout.headers.fields.status),
    parentItem: t(($) => $.loopVisualization.layout.headers.fields.parentItem),
    team: t(($) => $.loopVisualization.layout.headers.fields.team),
    itemDate: t(($) => $.loopVisualization.layout.headers.fields.date),
    createdBy: t(($) => $.loopVisualization.layout.headers.fields.createdBy),
    estimationId: t(($) => $.loopVisualization.layout.headers.fields.estimation),
  };
  const headerOptions = includeItemDate ? headerLabelsType : headerTypes;
  const itemOptions: ISelectOption[] = headerOptions.map((f) => ({
    value: f,
    label: headerFieldLabels[f],
  }));

  return [...customOptions, ...itemOptions];
};

export const isValidPeriodType = (value: string): value is PeriodType => {
  return PERIOD_TYPES.includes(value as PeriodType);
};

export const isHeaderStartDate = (value: string): boolean => {
  return (
    value.includes(':') &&
    value.split(':')[0] === 'startDate' &&
    isValidPeriodType(getDateHeaderPeriod(value))
  );
};

export const isHeaderEndDate = (value: string): boolean => {
  return (
    value.includes(':') &&
    value.split(':')[0] === 'endDate' &&
    isValidPeriodType(getDateHeaderPeriod(value))
  );
};

export const isHeaderDate = (value: string | null = null): boolean => {
  return !!value && (isHeaderStartDate(value) || isHeaderEndDate(value));
};

export function getDateHeaderPeriod(header: string): PeriodType {
  return header.split(':')[1] as PeriodType;
}

export const isHeaderCustomField = (value: string = ''): boolean => /^(\d+)$/.test(value);

export const isHeaderItemField = (value: string | null = null): boolean =>
  headerTypes.includes(value as (typeof headerTypes)[number]);

export const headerToCustomFieldId = (value: IPropertySelectorByID | null): number => {
  if (value?.type !== 'CustomPropertySelectorByID') {
    return 0;
  }
  const match = value.customPropertyID.match(/^(\d+)$/);
  return match ? Number(match[1]) : 0;
};

export const headerToCreatedByField = (value: IPropertySelectorByID | null): HeaderType | null =>
  value?.type === 'createdBy' ? 'createdBy' : null;

export const headerToItemField = (value: IPropertySelectorByID | null): HeaderType | null =>
  value?.type === 'CustomPropertySelectorByID' &&
  headerTypes.includes(value.customPropertyID as (typeof headerTypes)[number])
    ? (value.customPropertyID as HeaderType)
    : null;

export const headerToParentField = (value: IPropertySelectorByID | null): HeaderType | null =>
  value?.type === 'ParentItemSelector' ? 'parentItem' : null;

export const headerToDateField = (value: IPropertySelectorByID | null): string | null => {
  if (value?.type === 'DateSelector') {
    const selector = value as IDateHeaderSelector;
    return selector.header;
  }
  return null;
};

export const isVisualizationHeaderCustomField = (
  value: IVisualizationHeader,
): value is ICustomFieldHeader => {
  return value.type === 'CustomPropertySelectorByID' && isHeaderCustomField(value.customPropertyID);
};

export const isVisualizationHeaderItemStatus = (
  value: IVisualizationHeader,
): value is IItemStatusHeader => {
  return value.type === 'CustomPropertySelectorByID' && value.customPropertyID === 'status';
};

export const isVisualizationHeaderTeam = (value: IVisualizationHeader): value is ITeamHeader => {
  return value.type === 'CustomPropertySelectorByID' && value.customPropertyID === 'team';
};

export const isVisualizationHeaderEstimation = (
  value: IVisualizationHeader,
): value is IEstimationHeader => {
  return value.type === 'CustomPropertySelectorByID' && value.customPropertyID === 'estimationId';
};

export const isVisualizationHeaderPlan = (value: IVisualizationHeader): value is IPlanHeader => {
  return (
    value.type === 'CustomPropertySelectorByID' && value.customPropertyID === 'adaptivePlannerPlan'
  );
};

export const headerToFieldValue = (headerSelector: IPropertySelectorByID | null) => {
  const customFieldId = headerToCustomFieldId(headerSelector);
  return (
    headerToCreatedByField(headerSelector) ||
    headerToItemField(headerSelector) ||
    headerToParentField(headerSelector) ||
    headerToDateField(headerSelector) ||
    (customFieldId ? String(customFieldId) : null)
  );
};
